import axios from 'axios';

// Sanitize: strip trailing dots (DNS root) and trailing slashes from env var
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\.+\/*$/, '').replace(/\/+$/, '');

const millAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

// Handle 401 errors by redirecting to login
// ONLY redirect if we're on a /mill/* route (not from other portals)
millAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const pathname = window.location.pathname;

      // Don't redirect if already on any login page
      if (pathname.includes('/login')) {
        return Promise.reject(error);
      }

      // Only redirect to login if we're on a mill route
      // This prevents the interceptor from hijacking other pages
      if (pathname.startsWith('/mill')) {
        localStorage.removeItem('rbf_user_data');
        localStorage.removeItem('user');
        window.location.href = '/login?next=/mill/dashboard';
        return Promise.reject(error);
      }

      // For non-mill routes, just reject - let the appropriate handler deal with it
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Mill uses shared team auth, so /api/auth/me/ endpoint
export const millAuthAPI = {
  login: async (username: string, password: string) => {
    // Mill uses shared team login - this redirects to /login
    // But keep endpoint for backwards compatibility
    const response = await axios.post(`${API_BASE_URL}/token/`, {
      email: username,
      password,
    }, { withCredentials: true });
    return response.data;
  },
  getMe: () => millAPI.get('/auth/me/'),  // Use main team auth endpoint
  logout: async () => {
    try {
      await millAPI.post('/auth/logout/');  // Use main team auth endpoint
    } catch (e) {
      // Ignore errors
    }
    localStorage.removeItem('rbf_user_data');
    localStorage.removeItem('user');
  },
};

export const millInventoryAPI = {
  getAll: () => millAPI.get('/mill/inventory/'),
  getById: (id: number) => millAPI.get(`/mill/inventory/${id}/`),
  adjust: (id: number, data: { quantity: number; transaction_type: string; notes: string }) =>
    millAPI.post(`/mill/inventory/${id}/adjust/`, data),
  getLowStock: () => millAPI.get('/mill/inventory/low_stock/'),
};

export const millProductionAPI = {
  // Schedule - same endpoints as admin production
  getSchedule: (params?: { view?: string }) => millAPI.get('/production/schedule/', { params }),

  // Batch generation
  getOrdersByShipDate: (params: { start_date: string; end_date: string }) =>
    millAPI.get('/mill/production/orders-by-ship-date/', { params }),

  generateBatches: (data: { days_ahead?: number; dry_run?: boolean }) =>
    millAPI.post('/production/generate_batches/', data),

  // Individual batch operations
  getBatches: (params?: { status?: string }) => millAPI.get('/production/batches/', { params }),
  getBatchById: (id: number) => millAPI.get(`/production/batches/${id}/`),
  // Persist a new priority sequence: batch_ids in desired order → priority = index
  // (lower = higher). Drives the Plan-popup drag reorder (sub-step 3).
  // Endpoint = ProductionPortalViewSet.reorder (router basename 'production').
  reorderBatches: (batchIds: number[]) =>
    millAPI.post('/production/reorder/', { batch_ids: batchIds }),
  // Bulk-archive PLANNED batches (status flip -> ARCHIVED, hidden from plan views,
  // reversible via the archive_stale_batches --unarchive command). Returns
  // {success, archived, skipped} — skipped = ids not in PLANNED state.
  archiveBatches: (ids: number[]) =>
    millAPI.post('/production/archive_batches/', { batch_ids: ids }),
  // Operator-confirmed FIFO bank pull for one planned batch → shrinks the batch
  // to mill only the top-up (build 3-4). Returns {pulled_lbs, new_planned_weight}.
  confirmBankPull: (batchId: number) =>
    millAPI.post(`/production/batches/${batchId}/confirm_bank_pull/`, {}),
  startBatch: (id: number) => millAPI.post(`/production/batches/${id}/start/`),
  // completeBatch removed — /production/batches/<id>/complete/ is retired
  // (410 Gone); completion goes through completeMixing (disposition flow).
  cancelBatch: (id: number) => millAPI.post(`/production/batches/${id}/cancel/`),

  // Mixing endpoints
  getMixingBatch: (id: number) => millAPI.get(`/production/mixing/${id}/`),
  addIngredient: (id: number, data: { raw_material_id: number; quantity: number }) =>
    millAPI.post(`/production/mixing/${id}/add_ingredient/`, data),
  addAllIngredients: (id: number, ingredients: { raw_material_id: number; quantity: number }[]) =>
    millAPI.post(`/production/mixing/${id}/add_all_ingredients/`, { ingredients }),
  clearIngredients: (id: number) => millAPI.post(`/production/mixing/${id}/clear_ingredients/`),
  adjustBatchSize: (id: number, data: { new_weight: number }) =>
    millAPI.post(`/production/mixing/${id}/adjust_batch_size/`, data),
  checkInventory: (id: number) =>
    millAPI.post(`/production/mixing/${id}/check_inventory/`),
  completeMixing: (id: number, data?: {
    excess_destination?: string;
    excess_distribution?: Array<{ type: string; lbs: number; store_product_id?: number; bin_id?: number }>;
    close_order_items?: number[];
    make_up?: Array<{ order_item_id: number; lbs: number; source_type: 'BYOC_BIN' | 'FEED_BANK'; bin_id?: number; feed_bank_id?: number }>;
    notes?: string;
    force?: boolean;
  }) =>
    millAPI.post(`/production/mixing/${id}/complete_mixing/`, data),
  matchingStoreProducts: (id: number) =>
    millAPI.get(`/production/mixing/${id}/matching_store_products/`),
  matchingByocBins: (id: number) =>
    millAPI.get(`/production/mixing/${id}/matching_byoc_bins/`),
  matchingFeedBank: (id: number) =>
    millAPI.get(`/production/mixing/${id}/matching_feed_bank/`),
};

export const millPackagingAPI = {
  getList: (params?: { show_all?: boolean; show_completed?: boolean }) =>
    millAPI.get('/mill/packaging/', { params }),
  markPackaged: (batchId: number, allocationId: number) =>
    millAPI.post(`/mill/packaging/${batchId}/mark_packaged/`, { allocation_id: allocationId }),
  markBatchPackaged: (batchId: number) =>
    millAPI.post(`/mill/packaging/${batchId}/mark_batch_packaged/`),
};

// Packaging Station rebuild — pallet workspace (milled X-of-Y + build/edit/confirm + re-pack)
export const millPackAPI = {
  queue: () => millAPI.get('/mill/pack/queue/'),
  workspace: (orderId: number) => millAPI.get(`/mill/pack/${orderId}/`),
  persist: (orderId: number, regenerate = false) =>
    millAPI.post(`/mill/pack/${orderId}/persist/`, { regenerate }),
  confirm: (orderId: number) => millAPI.post(`/mill/pack/${orderId}/confirm/`),
  regenerateBol: (orderId: number) => millAPI.post(`/mill/pack/${orderId}/regenerate-bol/`),
  addFill: (palletId: number, data: { batch_id: number; formula_id: number; lbs: number; size: string }) =>
    millAPI.post(`/mill/pack/pallets/${palletId}/add-fill/`, data),
  moveFill: (targetPalletId: number, fillId: number) =>
    millAPI.post(`/mill/pack/pallets/${targetPalletId}/move-fill/`, { fill_id: fillId }),
  splitPallet: (palletId: number) => millAPI.post(`/mill/pack/pallets/${palletId}/split/`),
  mergePallets: (palletId: number, otherPalletId: number) =>
    millAPI.post(`/mill/pack/pallets/${palletId}/merge/`, { other_pallet_id: otherPalletId }),
  palletSheetsUrl: (orderId: number) => `/mill/orders/${orderId}/pallet-sheets/`,
  orderPalletSheets: (orderId: number) => millAPI.get(`/mill/orders/${orderId}/pallet-sheets/`),
};

export const millShippingAPI = {
  getList: () => millAPI.get('/mill/shipping/'),
  markShipped: (orderId: number, data?: { pro_number?: string; tracking_number?: string; carrier?: string; ship_date?: string; notes?: string }) =>
    millAPI.post(`/mill/shipping/${orderId}/mark_shipped/`, data),
  markReadyToShip: (orderId: number) =>
    millAPI.post(`/mill/shipping/${orderId}/mark_ready_to_ship/`),
};

// Bill of Lading API
export const bolAPI = {
  // List and detail
  getList: (params?: { status?: string }) => millAPI.get('/mill/bol/', { params }),
  getById: (id: number) => millAPI.get(`/mill/bol/${id}/`),

  // Get BOL for a specific order (for auto-populating shipping modal)
  getForOrder: (orderId: number) => millAPI.get('/mill/bol/for_order/', { params: { order_id: orderId } }),

  // Check if BOL already exists for order(s)
  checkExisting: (orderIds: number[]) => millAPI.get('/mill/bol/check_existing/', { params: { order_ids: orderIds.join(',') } }),

  // Create BOL
  create: (data: {
    order_ids: number[];
    bol_number: string;  // Required: Tracking/BOL # from shipper's barcode
    ship_date?: string;
    carrier?: string;
    driver_name?: string;
    trailer_number?: string;
    seal_number?: string;
    special_instructions?: string;
    reference_number?: string;
    quote_number?: string;
    pickup_number?: string;
    // Special services — print on the BOL; all accepted by the create endpoint.
    liftgate_required?: boolean;
    residential_delivery?: boolean;
    inside_delivery?: boolean;
    appointment_required?: boolean;
    notify_before_delivery?: boolean;
    replace_existing?: boolean;  // If true, void existing BOL and create new one
    // Quote-sourced (invoice-side): Sibyl's authoritative quoted numbers.
    is_quote_sourced?: boolean;
    quoted_pallet_count?: number;
    quoted_weight_lbs?: number;
  }) => millAPI.post('/mill/bol/', data),

  // Update BOL
  update: (id: number, data: {
    bol_number?: string;
    quote_number?: string;
    pro_number?: string;
    driver_name?: string;
    trailer_number?: string;
    seal_number?: string;
    carrier?: string;
    carrier_name?: string;
    ship_date?: string;
    estimated_delivery?: string;
    special_instructions?: string;
    customer_po_number?: string;
    reference_number?: string;
    pickup_number?: string;
    liftgate_required?: boolean;
    residential_delivery?: boolean;
    inside_delivery?: boolean;
    appointment_required?: boolean;
    notify_before_delivery?: boolean;
    // Consignee block — hand-editable; editing any field flags the BOL as
    // manually edited so autofill/regeneration won't revert it.
    consignee_name?: string;
    consignee_address?: string;
    consignee_city?: string;
    consignee_state?: string;
    consignee_zip?: string;
    consignee_phone?: string;
  }) => millAPI.patch(`/mill/bol/${id}/`, data),

  // Override the pallet build (combine/split/set count) — one entry per physical
  // pallet Brad ships; identical (weight, description) group into one BOL row. His
  // build is authoritative and guarded against regenerate/re-pack.
  setPalletBuild: (id: number, pallets: { weight_lbs: number; description?: string; nmfc_code?: string; freight_class?: string }[]) =>
    millAPI.post(`/mill/bol/${id}/set-pallet-build/`, { pallets }),

  // PDF operations
  generatePDF: (id: number) => millAPI.post(`/mill/bol/${id}/generate_pdf/`),
  downloadPDF: (id: number) => millAPI.get(`/mill/bol/${id}/download_pdf/`, { responseType: 'blob' }),

  // Regenerate the ONE existing BOL from the current persisted pallets
  // (after a re-pack). Same service both doors use — invoice screen and
  // mill shipping. Updates the record in place; never forks a second BOL.
  regenerateForOrder: (orderId: number) => millAPI.post(`/mill/pack/${orderId}/regenerate-bol/`),

  // Status changes
  markPrinted: (id: number) => millAPI.post(`/mill/bol/${id}/mark_printed/`),
  markShipped: (id: number, data?: { pro_number?: string; driver_name?: string; seal_number?: string }) =>
    millAPI.post(`/mill/bol/${id}/mark_shipped/`, data),
  markDelivered: (id: number, data?: { actual_delivery?: string; notes?: string }) =>
    millAPI.post(`/mill/bol/${id}/mark_delivered/`, data),
  void: (id: number, reason?: string) => millAPI.post(`/mill/bol/${id}/void/`, { reason }),
  // Post-shipment correction: void the shipped BOL and reissue a corrected DRAFT
  // reusing the same bol_number (override optional). Returns { bol_id }.
  reissue: (id: number, data: { reason: string; bol_number?: string }) =>
    millAPI.post(`/mill/bol/${id}/reissue/`, data),
  captureSignature: (id: number, data: { signature_type: 'shipper' | 'driver'; signature_data: string }) =>
    millAPI.post(`/mill/bol/${id}/capture_signature/`, data),
};

// Pallet Label API
export const palletLabelAPI = {
  // Get existing pallet labels for an order
  getForOrder: (orderId: number) => millAPI.get('/mill/pallet-labels/', { params: { order_id: orderId } }),

  // B5: returns the order's PERSISTED packed pallets (the same rows the
  // BOL reads) — no longer a bin-packing recompute. 409 if not packed.
  autoDistribute: (orderId: number) =>
    millAPI.post('/mill/pallet-labels/auto_distribute/', { order_id: orderId }),

  // Generate and save pallet labels — built server-side from the
  // persisted packed pallets; contents cannot be supplied by the client.
  generate: (data: {
    order_id: number;
    bol_id?: number;
  }) => millAPI.post('/mill/pallet-labels/generate/', data),

  // Preview labels without saving (returns base64 images) — rendered
  // server-side from the persisted packed pallets.
  preview: (data: {
    order_id: number;
    ship_date?: string;
    bol_number?: string;
  }) => millAPI.post('/mill/pallet-labels/preview/', data),

  // Print all pallet labels for an order
  printLabels: (orderId: number) => millAPI.post('/mill/pallet-labels/print_labels/', { order_id: orderId }),

  // Print a single pallet label
  printSingle: (labelId: number) => millAPI.post(`/mill/pallet-labels/${labelId}/print_single/`),
};

// Label printing API
export const labelAPI = {
  // Template management
  getTemplates: () => millAPI.get('/labels/templates/'),
  getTemplate: (id: number) => millAPI.get(`/labels/templates/${id}/`),
  previewTemplate: (id: number, params?: { milled_date?: string; batch_number?: string }) =>
    millAPI.get(`/labels/templates/${id}/preview/`, { params }),

  // Find best matching template for a formula
  findForFormula: (params: { formula_id?: number; form: string; formula_name?: string }) =>
    millAPI.get('/labels/templates/find_for_formula/', { params }),

  // CRUD for label management
  createTemplate: (formData: FormData) =>
    millAPI.post('/labels/templates/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateTemplate: (id: number, formData: FormData) =>
    millAPI.patch(`/labels/templates/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteTemplate: (id: number) => millAPI.delete(`/labels/templates/${id}/`),
  getFormulas: () => millAPI.get('/labels/templates/formulas/'),

  // Printing
  printLabels: (data: {
    allocation_id: number;
    template_id: number;
    quantity: number;
    milled_date?: string;
    batch_number?: string;
  }) => millAPI.post('/labels/print/print_labels/', data),

  printSingle: (data: {
    allocation_id: number;
    template_id: number;
  }) => millAPI.post('/labels/print/print_single/', data),

  getPrintCount: (allocationId: number) =>
    millAPI.get('/labels/print/print_count/', { params: { allocation_id: allocationId } }),

  // Print logs
  getPrintLogs: (params?: { batch_id?: number; order_id?: number; allocation_id?: number; limit?: number }) =>
    millAPI.get('/labels/logs/', { params }),
};

// Recall tool — batch → affected-customer trace + human-gated notice sender
export interface RecallTraceParams {
  batch_numbers?: string;   // comma-separated
  formula_id?: number | string;
  date_from?: string;       // YYYY-MM-DD
  date_to?: string;
}

export const recallAPI = {
  trace: (params: RecallTraceParams) =>
    millAPI.get('/mill/recall/trace/', { params }),
  traceCsv: (params: RecallTraceParams) =>
    millAPI.get('/mill/recall/trace/', {
      params: { ...params, export: 'csv' },
      responseType: 'blob',
    }),
  // Two-step: omit confirm (or false) → preview only, nothing sends.
  notify: (data: RecallTraceParams & {
    reason?: string;
    action_required?: string;
    subject?: string;
    extra_note?: string;
    confirm?: boolean;
  }) => millAPI.post('/mill/recall/notify/', data),
};

// Store alerts API for Mill Portal
export const millStoreAPI = {
  getStoreAlerts: () => millAPI.get('/mill/store/alerts/'),
  getStoreSummary: () => millAPI.get('/mill/store/summary/'),
  acknowledgeAlert: (id: number) => millAPI.post(`/mill/store/alerts/${id}/acknowledge/`),
  dismissAlert: (id: number) => millAPI.post(`/mill/store/alerts/${id}/dismiss/`),
  resolveAlert: (id: number) => millAPI.post(`/mill/store/alerts/${id}/resolve/`),
  startBatch: (id: number) => millAPI.post(`/mill/store/alerts/${id}/start-batch/`),
  pullFromBank: (id: number, data?: { confirm?: boolean; quantity_lbs?: number }) =>
    millAPI.post(`/mill/store/alerts/${id}/pull-from-bank/`, data),
};

// Orders Overview API for Mill Portal
export interface OrderItemInfo {
  id: number;
  product_name: string;
  quantity: number;
  unit: string;
  weight_lbs: number | null;
  batch_info: { batch_number: string; batch_status: string } | null;
}

export interface OrderOverview {
  id: number;
  order_number: string;
  customer_name: string;
  customer_id: number;
  status: string;
  status_display: string;
  delivery_date: string | null;
  shipping_method: string;
  shipping_method_display: string;
  total_weight_lbs: number;
  items: OrderItemInfo[];
  has_pending_modification: boolean;
  pending_modification: {
    id: number;
    modification_type: string;
    description: string;
    modified_by_type: string;
    created_at: string;
  } | null;
  is_rush: boolean;
  notes: string | null;
  special_instructions: string | null;
  source?: string;
  is_bridge_club?: boolean;
}

export interface ModificationOverview {
  id: number;
  order_id: number;
  order_number: string;
  customer_name: string;
  modification_type: string;
  modification_type_display: string;
  description: string;
  modified_by_type: string;
  modified_by_name: string | null;
  created_at: string;
  status: string;
  original_delivery_date: string | null;
  adjusted_delivery_date: string | null;
}

export const millOrdersAPI = {
  // Get orders overview by timeframe with status counts
  getOverview: (params?: { timeframe?: 'today' | 'this_week' | 'next_week' | 'all_pending' }) =>
    millAPI.get<{
      timeframe: string;
      orders_by_date: { [date: string]: OrderOverview[] };
      status_counts: { [key: string]: number };
      total_orders: number;
    }>('/mill/orders-overview/', { params }),

  // Get all pending modifications
  getPendingModifications: () =>
    millAPI.get<{
      modifications: ModificationOverview[];
      total_pending: number;
    }>('/mill/pending-modifications/'),

  // Approve a modification
  approveModification: (id: number, data?: { adjust_date?: boolean; new_delivery_date?: string; notes?: string }) =>
    millAPI.post(`/mill/modifications/${id}/approve/`, data),

  // Reject a modification
  rejectModification: (id: number, data?: { reason?: string }) =>
    millAPI.post(`/mill/modifications/${id}/reject/`, data),
};

// Store inventory item (bagged products visible to mill)
export interface StoreInventoryItem {
  id: number;
  product_id: number;
  product_name: string;
  sku: string;
  category: string;
  category_display: string;
  form: string;
  size_lbs: number;
  feed_formula_id: number | null;
  feed_formula_name: string | null;
  location: string;
  location_display: string;
  quantity: number;
  reserved_quantity: number;
  available_quantity: number;
  reorder_threshold: number;
  needs_reorder: boolean;
  last_updated: string | null;
}

// Enhanced bulk bin with low-stock info
export interface StoreBulkBin {
  id: number;
  name: string;
  product_name: string;
  feed_formula_id: number | null;
  current_lbs: number;
  reserved_lbs: number;
  available_lbs: number;
  capacity_lbs: number;
  percentage_full: number;
  needs_refill: boolean;
  last_filled: string | null;
}

export interface StoreOverviewResponse {
  bagged_inventory: StoreInventoryItem[];
  bulk_bins: StoreBulkBin[];
  summary: {
    total_bags: number;
    low_stock_bags: number;
    total_bins: number;
    low_bins: number;
  };
}

// Feed Bank API for managing excess feed distribution
export const feedBankAPI = {
  // Get all feed bank entries
  getAll: () => millAPI.get('/production/feed-bank/'),

  // Get summary of feed bank status
  getSummary: () => millAPI.get('/production/feed-bank/summary/'),

  // Compact always-on availability indicator: total lbs, lot count, oldest age,
  // fresh/aging/stale bucket counts (build 3-5).
  getAvailabilitySummary: () => millAPI.get('/production/feed-bank/availability_summary/'),

  // Get available feed bank entries (not yet used)
  getAvailable: () => millAPI.get('/production/feed-bank/available/'),

  // Get bulk bins available for distribution
  getBulkBins: () => millAPI.get('/production/feed-bank/bulk_bins/'),

  // Get store inventory overview (bagged + bins)
  getStoreOverview: () => millAPI.get<StoreOverviewResponse>('/production/feed-bank/store_overview/'),

  // Mark feed as used (for internal use)
  use: (id: number, data: { quantity: number; notes?: string }) =>
    millAPI.post(`/production/feed-bank/${id}/use/`, data),

  // Distribute feed to a bulk bin for retail
  distributeToBulkBin: (id: number, data: { bin_id: number; quantity_lbs: number; notes?: string }) =>
    millAPI.post(`/production/feed-bank/${id}/distribute_to_bulk_bin/`, data),

  // Manually add a feed bank entry
  create: (data: { formula_id: number; feed_form: string; quantity_lbs: number; destination?: string; notes?: string }) =>
    millAPI.post('/production/feed-bank/', data),

  // Edit a feed bank entry
  update: (id: number, data: { quantity_lbs?: number; destination?: string; notes?: string }) =>
    millAPI.patch(`/production/feed-bank/${id}/`, data),

  // Delete a feed bank entry
  delete: (id: number) => millAPI.delete(`/production/feed-bank/${id}/`),

  // Get matching pending orders for allocation
  getMatchingOrders: (id: number) => millAPI.get(`/production/feed-bank/${id}/matching_orders/`),

  // Allocate feed bank entry to an order item
  allocateToOrder: (id: number, data: { order_item_id: number; quantity_lbs?: number }) =>
    millAPI.post(`/production/feed-bank/${id}/allocate_to_order/`, data),
};

// Production Plan API for auto-population feature
export interface ProductionPlanSource {
  source_type: 'PRODUCTION' | 'FEED_BANK' | 'STORE_BAGGED' | 'BYOC_BIN';
  source_id?: number;
  source_name?: string;
  quantity_lbs: number;
  quantity_bags?: number;
  bag_size?: string;
  status: 'AVAILABLE' | 'NEEDS_BATCH';
  entries?: Array<{ id: number; quantity_lbs: number }>;
}

export interface ProductionPlanOrderItem {
  order_item_id: number;
  order_id: number;
  order_number: string;
  customer_name: string;
  delivery_date: string | null;
  formula_id: number;
  formula_name: string;
  feed_form: 'MASH' | 'PELLET';
  package_type: string;
  package_type_display: string;
  total_quantity_lbs: number;
  number_of_packages: number;
  sources: ProductionPlanSource[];
  needs_production_lbs: number;
  percent_fulfilled: number;
  status: string;
  source?: string;
  is_bridge_club?: boolean;
}

export interface ProductionPlanBatch {
  formula_id: number;
  formula_name: string;
  feed_form: 'MASH' | 'PELLET';
  batch_size_lbs: number;
  for_orders_lbs: number;
  excess_lbs: number;
  planned_date: string | null;
  orders: Array<{
    order_item_id: number;
    order_number: string;
    customer_name: string;
    quantity_lbs: number;
  }>;
  status: string;
}

export interface PlannedBatch {
  id: number;
  batch_number: string;
  formula_name: string;
  feed_form: string;
  planned_weight: number;
  planned_date: string | null;
  orders: Array<{
    order_number: string;
    customer_name: string;
    allocated_weight: number;
  }>;
  notes: string;
}

export interface ActivityEntry {
  batch_number: string;
  formula_name: string;
  feed_form: string;
  planned_weight: number;
  created_at: string | null;
  description: string;
}

export interface ProductionPlan {
  order_items: ProductionPlanOrderItem[];
  production_needs: ProductionPlanBatch[];
  planned_batches: PlannedBatch[];
  recent_activity: ActivityEntry[];
  summary: {
    total_order_items: number;
    items_needing_production: number;
    items_from_inventory: number;
    total_batches_needed: number;
    total_lbs_to_produce: number;
    status_counts: { [key: string]: number };
  };
  planning_window: {
    start_date: string;
    end_date: string;
    days: number;
  };
}

export const productionPlanAPI = {
  // Get the production plan (auto-calculated)
  getPlan: (days?: number) =>
    millAPI.get<ProductionPlan>('/mill/production-plan/', { params: days ? { days } : undefined }),

  // Pull from store inventory (creates store task)
  pullFromStore: (data: {
    order_item_id: number;
    store_inventory_id: number;
    quantity_bags: number;
  }) => millAPI.post('/mill/production-plan/pull-from-store/', data),

  // Pull from Feed Bank
  pullFromBank: (data: {
    order_item_id: number;
    feed_bank_id: number;
    quantity_lbs: number;
  }) => millAPI.post('/mill/production-plan/pull-from-bank/', data),
};

// Store Tasks API for store employees
export interface StoreTaskItem {
  id: number;
  feed_formula_id?: number;
  feed_formula_name: string;
  feed_form: string;
  source_type: 'BAGGED' | 'BYOC';
  source_type_display?: string;
  quantity_lbs: number;
  quantity_bags: number | null;
  bag_size: string | null;
  order_number: string;
  customer_name: string;
  is_pulled: boolean;
  pulled_at: string | null;
  pulled_by?: string | null;
}

export interface StoreTask {
  id: number;
  task_type: 'PULL_BAGGED' | 'PULL_BYOC';
  task_type_display: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  status_display: string;
  priority: 'NORMAL' | 'URGENT';
  priority_display: string;
  due_date: string;
  created_at: string;
  created_by?: string | null;
  completed_at?: string | null;
  completed_by?: string | null;
  notes?: string;
  total_items: number;
  pulled_items: number;
  is_complete: boolean;
  items: StoreTaskItem[];
}

export const storeTasksAPI = {
  // Get list of store tasks
  getList: (status?: string) =>
    millAPI.get<{ tasks: StoreTask[]; total_count: number }>(
      '/store/tasks/',
      { params: status ? { status } : undefined }
    ),

  // Get a single task with details
  getById: (id: number) => millAPI.get<StoreTask>(`/store/tasks/${id}/`),

  // Start working on a task
  start: (id: number) => millAPI.post(`/store/tasks/${id}/start/`),

  // Mark an item as pulled
  markItemPulled: (taskId: number, itemId: number) =>
    millAPI.post(`/store/tasks/${taskId}/items/${itemId}/mark-pulled/`),

  // Complete a task manually
  complete: (id: number) => millAPI.post(`/store/tasks/${id}/complete/`),

  // Cancel a task
  cancel: (id: number) => millAPI.post(`/store/tasks/${id}/cancel/`),
};

export default millAPI;
